import { type NavigationItem } from '@af/design-system-docs-ui';

/**
 * `navigation` is the object passed to `@atlaskit/navigation-system`. It defines the structure (order and hierarchy)
 * of the side navigation items. It does NOT define the resolvable routes (resolved routes are currently defined by a combination
 * of file-system routing, and package docs ).
 */
export const navigation: NavigationItem[] = [
	{
		item: { title: 'Get started', to: '/get-started', slug: 'get-started' },
		subitems: [
			{
				item: {
					title: 'Design',
					to: '/get-started/design',
					slug: 'design',
				},
				subitems: [
					{
						item: {
							title: 'Figma libraries',
							to: '/get-started/design/figma-libraries',
							slug: 'figma-libraries',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Develop',
					to: '/get-started/develop',
					slug: 'develop',
				},
				subitems: [
					{
						item: {
							title: 'Composing code',
							to: '/get-started/develop/composition',
							slug: 'composition',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Content design',
					to: '/get-started/content-design',
					slug: 'content-design',
				},
				subitems: [],
			},
			{
				item: {
					title: 'About the design system',
					to: '/get-started/about-atlassian-design-system',
					slug: 'about-atlassian-design-system',
				},
				subitems: [],
			},
		],
	},
	{
		item: { title: 'Foundations', to: '/foundations', slug: 'foundations' },
		subitems: [
			{
				item: { title: 'Tokens', to: '/foundations/tokens', slug: 'tokens' },
				subitems: [
					{
						item: {
							title: 'All design tokens',
							to: '/components/tokens/all-tokens',
							slug: 'all-tokens',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Design tokens explained',
							to: '/foundations/tokens/design-tokens',
							slug: 'design-tokens',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Use tokens in code',
							to: '/foundations/tokens/use-tokens-in-code',
							slug: 'use-tokens-in-code',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Use tokens in design',
							to: '/foundations/tokens/use-tokens-in-design',
							slug: 'use-tokens-in-design',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Migrate to tokens',
							to: '/foundations/tokens/migrate-to-tokens',
							slug: 'migrate-to-tokens',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Accessibility',
					to: '/foundations/accessibility',
					slug: 'accessibility',
				},
				subitems: [],
			},
			{
				item: { title: 'Content', to: '/foundations/content', slug: 'content' },
				subitems: [
					{
						item: {
							title: 'Inclusive language',
							to: '/foundations/content/inclusive-writing',
							slug: 'inclusive-writing',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Style, grammar, and punctuation',
							to: '/foundations/content/language-and-grammar',
							slug: 'language-and-grammar',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Voice and tone',
							to: '/foundations/content/voice-tone',
							slug: 'voice-tone',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Date and time',
							to: '/foundations/content/date-time',
							slug: 'date-time',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Designing messages',
							to: '/foundations/content/designing-messages',
							slug: 'designing-messages',
						},
						subitems: [
							{
								item: {
									title: 'Empty state',
									to: '/foundations/content/designing-messages/empty-state',
									slug: 'empty-state',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Error messages',
									to: '/foundations/content/designing-messages/error-messages',
									slug: 'error-messages',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Feature discovery',
									to: '/foundations/content/designing-messages/feature-discovery',
									slug: 'feature-discovery',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Info messages',
									to: '/foundations/content/designing-messages/info-messages',
									slug: 'info-messages',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Success messages',
									to: '/foundations/content/designing-messages/success-messages',
									slug: 'success-messages',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Warning messages',
									to: '/foundations/content/designing-messages/warning-messages',
									slug: 'warning-messages',
								},
								subitems: [],
							},
						],
					},
				],
			},
			{
				item: { title: 'Spacing', to: '/foundations/spacing', slug: 'spacing' },
				subitems: [
					{
						item: {
							title: 'Layout primitives',
							to: '/foundations/spacing/primitives',
							slug: 'primitives',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Grid',
					to: '/foundations/grid',
					slug: 'grid',
				},
				subitems: [],
			},
			{
				item: { title: 'Color', to: '/foundations/color', slug: 'color' },
				subitems: [
					{
						item: { title: 'Accents', to: '/foundations/color/accents', slug: 'accents' },
						subitems: [],
					},
					{
						item: {
							title: 'Color picker swatches',
							to: '/foundations/color/color-picker-swatches',
							slug: 'color-picker-swatches',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Data Visualization',
							to: '/foundations/color/data-visualization-color',
							slug: 'data-visualization-color',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Color palette',
							to: '/foundations/color/color-palette',
							slug: 'color-palette',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Typography',
					to: '/foundations/typography',
					slug: 'typography',
				},
				subitems: [
					{
						item: {
							title: 'Applying typography',
							to: '/foundations/typography/applying-typography',
							slug: 'applying-typography',
						},
						subitems: [],
					},
					{
						item: {
							title: 'App typefaces and scale',
							to: '/foundations/typography/product-typefaces-and-scale',
							slug: 'product-typefaces-and-scale',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Motion',
					to: '/foundations/motion',
					slug: 'motion',
				},
				subitems: [
					{
						item: {
							title: 'Applying motion',
							to: '/foundations/motion/applying-motion',
							slug: 'applying-motion',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Iconography',
					to: '/foundations/iconography',
					slug: 'iconography',
				},
				subitems: [],
			},
			{
				item: {
					title: 'Illustrations',
					to: '/foundations/illustrations',
					slug: 'illustrations',
				},
				subitems: [],
			},
			{
				item: { title: 'Logos', to: '/foundations/logos', slug: 'logos' },
				subitems: [],
			},
			{
				item: { title: 'Elevation', to: '/foundations/elevation', slug: 'elevation' },
				subitems: [],
			},
			{
				item: {
					title: 'Border',
					to: '/foundations/border',
					slug: 'border',
					status: {
						type: 'beta',
					},
				},
				subitems: [],
			},
			{
				item: {
					title: 'Radius',
					to: '/foundations/radius',
					slug: 'radius',
					status: {
						type: 'beta',
					},
				},
				subitems: [],
			},
		],
	},
	{
		item: { title: 'Components', to: '/components', slug: 'components' },

		subitems: [
			{
				item: {
					title: 'Forms and input',
				},
				subitems: [
					{
						item: {
							title: 'Button',
							slug: 'button',
							to: '/components/button',
						},
						subitems: [
							{
								item: {
									title: 'Icon button',
									slug: 'icon-button',
									to: '/components/button/icon-button',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Link button',
									slug: 'link-button',
									to: '/components/button/link-button',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Link icon button',
									slug: 'link-icon-button',
									to: '/components/button/link-icon-button',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Split button',
									slug: 'split-button',
									to: '/components/button/split-button',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Button group',
									slug: 'button-group',
									to: '/components/button/button-group',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Button (legacy)',
									slug: 'button-legacy',
									to: '/components/button/button-legacy',
									status: {
										type: 'intent-to-deprecate',
									},
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Calendar',
							slug: 'calendar',
							to: '/components/calendar',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Checkbox',
							slug: 'checkbox',
							to: '/components/checkbox',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Comment',
							slug: 'comment',
							to: '/components/comment',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Date time picker',
							slug: 'datetime-picker',
							to: '/components/datetime-picker',
						},
						subitems: [
							{
								item: {
									title: 'Date picker',
									slug: 'date-picker',
									to: '/components/datetime-picker/date-picker',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Time picker',
									slug: 'time-picker',
									to: '/components/datetime-picker/time-picker',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Dropdown menu',
							slug: 'dropdown-menu',
							to: '/components/dropdown-menu',
						},
						subitems: [
							{
								item: {
									title: 'Dropdown item',
									slug: 'dropdown-item',
									to: '/components/dropdown-menu/dropdown-item',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Dropdown item checkbox',
									slug: 'dropdown-item-checkbox',
									to: '/components/dropdown-menu/dropdown-item-checkbox',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Dropdown item radio',
									slug: 'dropdown-item-radio',
									to: '/components/dropdown-menu/dropdown-item-radio',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Focus ring',
							slug: 'focus-ring',
							to: '/components/focus-ring',
							status: {
								type: 'intent-to-deprecate',
							},
						},
						subitems: [],
					},
					{
						item: {
							title: 'Form',
							slug: 'form',
							to: '/components/form',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Radio',
							slug: 'radio',
							to: '/components/radio',
						},
						subitems: [
							{
								item: {
									title: 'Radio group',
									slug: 'radio-group',
									to: '/components/radio/radio-group',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Range',
							slug: 'range',
							to: '/components/range',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Select',
							slug: 'select',
							to: '/components/select',
						},
						subitems: [
							{
								item: {
									title: 'Async creatable select',
									slug: 'async-creatable-select',
									to: '/components/select/async-creatable-select',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Async select',
									slug: 'async-select',
									to: '/components/select/async-select',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Checkbox select',
									slug: 'checkbox-select',
									to: '/components/select/checkbox-select',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Country select',
									slug: 'country-select',
									to: '/components/select/country-select',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Creatable select',
									slug: 'creatable-select',
									to: '/components/select/creatable-select',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Popup select',
									slug: 'popup-select',
									to: '/components/select/popup-select',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Radio select',
									slug: 'radio-select',
									to: '/components/select/radio-select',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Text area',
							slug: 'textarea',
							to: '/components/textarea',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Text field',
							slug: 'textfield',
							to: '/components/textfield',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Toggle',
							slug: 'toggle',
							to: '/components/toggle',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Images and icons',
				},
				subitems: [
					{
						item: {
							title: 'Avatar',
							slug: 'avatar',
							to: '/components/avatar',
						},
						subitems: [
							{
								item: {
									title: 'Avatar content',
									slug: 'avatar-content',
									to: '/components/avatar/avatar-content',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Avatar item',
									slug: 'avatar-item',
									to: '/components/avatar/avatar-item',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Avatar skeleton',
									slug: 'avatar-skeleton',
									to: '/components/avatar/avatar-skeleton',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Avatar group',
							slug: 'avatar-group',
							to: '/components/avatar-group',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Icon',
							slug: 'icon',
							to: '/components/icon',
						},
						subitems: [
							{
								item: {
									title: 'Icon tile',
									slug: 'icon-tile',
									to: '/components/icon/icon-tile',
								},
								subitems: [],
							},
						],
					},

					{
						item: {
							title: 'Image',
							slug: 'image',
							to: '/components/image',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Object',
							slug: 'object',
							to: '/components/object',
						},
						subitems: [
							{
								item: {
									title: 'Object tile',
									slug: 'object-tile',
									to: '/components/object/object-tile',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Logo',
							slug: 'logo',
							to: '/components/logo',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Tile',
							slug: 'tile',
							to: '/components/tile',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Labels',
				},
				subitems: [
					{
						item: {
							title: 'Badge',
							slug: 'badge',
							to: '/components/badge',
						},
						subitems: [
							{
								item: {
									title: 'Badge',
									slug: 'badge',
									to: '/components/badge/badge',
									status: {
										type: 'intent-to-deprecate',
									},
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Date label',
							slug: 'date-label',
							to: '/components/date-label',
							status: {
								type: 'beta',
							},
						},
						subitems: [
							{
								item: {
									title: 'Date label dropdown trigger',
									slug: 'date-label-dropdown-trigger',
									to: '/components/date-label/date-label-dropdown-trigger',
									status: {
										type: 'beta',
									},
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Lozenge',
							slug: 'lozenge',
							to: '/components/lozenge',
						},
						subitems: [
							{
								item: {
									title: 'Lozenge',
									slug: 'examples',
									to: '/components/lozenge/lozenge',
									status: {
										type: 'intent-to-deprecate',
									},
								},
								subitems: [],
							},
							{
								item: {
									title: 'Lozenge dropdown trigger',
									slug: 'lozenge-dropdown-trigger',
									to: '/components/lozenge/lozenge-dropdown-trigger',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Tag',
							slug: 'tag',
							to: '/components/tag',
						},
						subitems: [
							{
								item: {
									title: 'Tag',
									slug: 'tag',
									to: '/components/tag/tag',
									status: {
										type: 'intent-to-deprecate',
									},
								},
								subitems: [],
							},
							{
								item: {
									title: 'Avatar tag',
									slug: 'avatar-tag',
									to: '/components/tag/avatar-tag',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Tag group',
							slug: 'tag-group',
							to: '/components/tag-group',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Layout and structure',
				},
				subitems: [
					{
						item: {
							title: 'Page',

							slug: 'page',
							to: '/components/page',
							status: {
								type: 'intent-to-deprecate',
							},
						},
						subitems: [
							{
								item: {
									title: 'Grid',
									slug: 'grid',
									to: '/components/page/grid',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Grid column',
									slug: 'grid-column',
									to: '/components/page/grid-column',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Page header',
							slug: 'page-header',
							to: '/components/page-header',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Panel',
							slug: 'panel',
							to: '/components/panel',
							status: {
								type: 'beta',
							},
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Loading',
				},
				subitems: [
					{
						item: {
							title: 'Progress bar',
							slug: 'progress-bar',
							to: '/components/progress-bar',
						},
						subitems: [
							{
								item: {
									title: 'Success progress bar',
									slug: 'success-progress-bar',
									to: '/components/progress-bar/success-progress-bar',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Transparent progress bar',
									slug: 'transparent-progress-bar',
									to: '/components/progress-bar/transparent-progress-bar',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Skeleton',
							slug: 'skeleton',
							to: '/components/skeleton',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Spinner',
							slug: 'spinner',
							to: '/components/spinner',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Messaging',
				},
				subitems: [
					{
						item: {
							title: 'Banner',
							slug: 'banner',
							to: '/components/banner',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Empty state',
							slug: 'empty-state',
							to: '/components/empty-state',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Flag',
							slug: 'flag',
							to: '/components/flag',
						},
						subitems: [
							{
								item: {
									title: 'Auto dismiss flag',
									slug: 'auto-dismiss-flag',
									to: '/components/flag/auto-dismiss-flag',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Flag group',
									slug: 'flag-group',
									to: '/components/flag/flag-group',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Flags provider',
									slug: 'flags-provider',
									to: '/components/flag/flags-provider',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Inline message',
							slug: 'inline-message',
							to: '/components/inline-message',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Modal dialog',
							slug: 'modal-dialog',
							to: '/components/modal-dialog',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Spotlight',
							slug: 'spotlight',
							to: '/components/spotlight',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Section message',

							slug: 'section-message',
							to: '/components/section-message',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Navigation',
				},
				subitems: [
					{
						item: {
							title: 'Breadcrumbs',
							slug: 'breadcrumbs',
							to: '/components/breadcrumbs',
						},
						subitems: [
							{
								item: {
									title: 'Breadcrumbs',
									slug: 'breadcrumbs',
									to: '/components/breadcrumbs/breadcrumbs',
									status: {
										type: 'beta',
									},
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Link',
							slug: 'link',
							to: '/components/link',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Menu',
							slug: 'menu',
							to: '/components/menu',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Navigation system',
							slug: 'navigation-system',
							to: '/components/navigation-system',
						},
						subitems: [
							{
								item: {
									title: 'Layout',
									slug: 'layout',
									to: '/components/navigation-system/layout',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Side nav items',
									slug: 'side-nav-items',
									to: '/components/navigation-system/side-nav-items',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Top nav items',
									slug: 'top-nav-items',
									to: '/components/navigation-system/top-nav-items',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Pagination',
							slug: 'pagination',
							to: '/components/pagination',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Tabs',
							slug: 'tabs',
							to: '/components/tabs',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Overlays and layering',
				},
				subitems: [
					{
						item: {
							title: 'Blanket',
							slug: 'blanket',
							to: '/components/blanket',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Drawer',
							slug: 'drawer',
							to: '/components/drawer',
							status: {
								type: 'intent-to-deprecate',
							},
						},
						subitems: [],
					},
					{
						item: {
							title: 'Inline dialog',
							slug: 'inline-dialog',
							to: '/components/inline-dialog',
							status: {
								type: 'intent-to-deprecate',
							},
						},
						subitems: [],
					},
					{
						item: {
							title: 'Popup',
							slug: 'popup',
							to: '/components/popup',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Tooltip',
							slug: 'tooltip',
							to: '/components/tooltip',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Primitives',
				},
				subitems: [
					{
						item: {
							title: 'Overview',
							slug: 'overview',
							to: '/components/primitives/overview',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Box',
							slug: 'box',
							to: '/components/primitives/box',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Inline',
							slug: 'inline',
							to: '/components/primitives/inline',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Stack',
							slug: 'stack',
							to: '/components/primitives/stack',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Flex',
							slug: 'flex',
							to: '/components/primitives/flex',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Grid',
							slug: 'grid',
							to: '/components/primitives/grid',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Bleed',
							slug: 'bleed',
							to: '/components/primitives/bleed',
						},
						subitems: [],
					},
					{
						item: {
							title: 'XCSS',
							slug: 'xcss',
							to: '/components/primitives/xcss',
							status: {
								type: 'intent-to-deprecate',
							},
						},
						subitems: [],
					},
					{
						item: {
							title: 'Responsive',
							slug: 'responsive',
							to: '/components/primitives/responsive',
						},
						subitems: [
							{
								item: {
									title: 'Show',
									slug: 'show',
									to: '/components/primitives/responsive/show',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Hide',
									slug: 'hide',
									to: '/components/primitives/responsive/hide',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Breakpoints',
									slug: 'breakpoints',
									to: '/components/primitives/responsive/breakpoints',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Text',
							slug: 'text',
							to: '/components/primitives/text',
						},
						subitems: [],
					},
					{
						item: {
							title: 'MetricText',
							slug: 'metric-text',
							to: '/components/primitives/metric-text',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Pressable',
							slug: 'pressable',
							to: '/components/primitives/pressable',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Anchor',
							slug: 'anchor',
							to: '/components/primitives/anchor',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Focusable',
							slug: 'focusable',
							to: '/components/primitives/focusable',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Status indicators',
				},
				subitems: [
					{
						item: {
							title: 'Progress indicator',
							slug: 'progress-indicator',
							to: '/components/progress-indicator',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Progress tracker',
							slug: 'progress-tracker',
							to: '/components/progress-tracker',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Text and data display',
				},
				subitems: [
					{
						item: {
							title: 'Code',
							slug: 'code',
							to: '/components/code',
						},
						subitems: [
							{
								item: {
									title: 'Code block',
									slug: 'code-block',
									to: '/components/code/code-block',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Dynamic table',
							slug: 'dynamic-table',
							to: '/components/dynamic-table',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Heading',
							slug: 'heading',
							to: '/components/heading',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Inline edit',
							slug: 'inline-edit',
							to: '/components/inline-edit',
						},
						subitems: [
							{
								item: {
									title: 'Inline editable textfield',
									slug: 'inline-editable-textfield',
									to: '/components/inline-edit/inline-editable-textfield',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Table',
							slug: 'table',
							to: '/components/table',
							status: {
								type: 'intent-to-deprecate',
							},
						},
						subitems: [],
					},
					{
						item: {
							title: 'Table tree',
							slug: 'table-tree',
							to: '/components/table-tree',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Visually hidden',
							slug: 'visually-hidden',
							to: '/components/visually-hidden',
						},
						subitems: [],
					},
				],
			},
			{
				item: {
					title: 'Libraries',
				},
				subitems: [
					{
						item: {
							title: 'CSS',
							slug: 'css',
							to: '/components/css',
							status: {
								type: 'beta',
							},
						},
						subitems: [],
					},
					{
						item: {
							title: 'CSS reset',
							slug: 'css-reset',
							to: '/components/css-reset',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Design tokens',
							slug: 'tokens',
							to: '/components/tokens',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Motion',
							slug: 'motion',
							to: '/components/motion',
						},
						subitems: [
							{
								item: {
									title: 'Motion primitive',
									slug: 'motion-primitive',
									to: '/components/motion/motion-primitive',
									status: {
										type: 'alpha',
									},
								},
								subitems: [],
							},
							{
								item: {
									title: 'Resizing motion',
									slug: 'resizing-motion',
									to: '/components/motion/resizing-motion',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Exiting persistence',
									slug: 'exiting-persistence',
									to: '/components/motion/exiting-persistence',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Staggered entrance',
									slug: 'staggered-entrance',
									to: '/components/motion/staggered-entrance',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Accessibility',
									slug: 'accessibility',
									to: '/components/motion/accessibility',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Entering motion (legacy)',
									slug: 'entering-motion',
									to: '/components/motion/entering-motion',
									status: {
										type: 'intent-to-deprecate',
									},
								},
								subitems: [],
							},
							{
								item: {
									title: 'Variables (legacy)',
									slug: 'variables',
									to: '/components/motion/variables',
									status: {
										type: 'intent-to-deprecate',
									},
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Popper',
							slug: 'popper',
							to: '/components/popper',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Portal',
							slug: 'portal',
							to: '/components/portal',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Pragmatic drag and drop',
							slug: 'pragmatic-drag-and-drop',
							to: '/components/pragmatic-drag-and-drop',
						},
						subitems: [
							{
								item: {
									title: 'Examples',
									slug: 'examples',
									to: '/components/pragmatic-drag-and-drop/examples',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Tutorial',
									slug: 'tutorial',
									to: '/components/pragmatic-drag-and-drop/tutorial',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Core package',
									slug: 'core-package',
									to: '/components/pragmatic-drag-and-drop/core-package',
								},
								subitems: [
									{
										item: {
											title: 'Adapters',
											slug: 'adapters',
											to: '/components/pragmatic-drag-and-drop/core-package/adapters',
										},
										subitems: [
											{
												item: {
													title: 'Element',
													slug: 'element',
													to: '/components/pragmatic-drag-and-drop/core-package/adapters/element',
												},
												subitems: [],
											},
											{
												item: {
													title: 'Text selection',
													slug: 'text-selection',
													to: '/components/pragmatic-drag-and-drop/core-package/adapters/text-selection',
												},
												subitems: [],
											},
											{
												item: {
													title: 'External',
													slug: 'external',
													to: '/components/pragmatic-drag-and-drop/core-package/adapters/external',
												},
												subitems: [],
											},
										],
									},
									{
										item: {
											title: 'Drop targets',
											slug: 'drop-targets',
											to: '/components/pragmatic-drag-and-drop/core-package/drop-targets',
										},
										subitems: [],
									},
									{
										item: {
											title: 'Monitors',
											slug: 'monitors',
											to: '/components/pragmatic-drag-and-drop/core-package/monitors',
										},
										subitems: [],
									},
									{
										item: {
											title: 'Utilities',
											slug: 'utilities',
											to: '/components/pragmatic-drag-and-drop/core-package/utilities',
										},
										subitems: [],
									},
									{
										item: {
											title: 'Events',
											slug: 'events',
											to: '/components/pragmatic-drag-and-drop/core-package/events',
										},
										subitems: [],
									},
									{
										item: {
											title: 'Reconciliation',
											slug: 'reconciliation',
											to: '/components/pragmatic-drag-and-drop/core-package/reconciliation',
										},
										subitems: [],
									},
									{
										item: {
											title: 'UI frameworks',
											slug: 'UI-frameworks',
											to: '/components/pragmatic-drag-and-drop/core-package/UI-frameworks',
										},
										subitems: [],
									},
									{
										item: {
											title: 'Recipes',
											slug: 'recipes',
											to: '/components/pragmatic-drag-and-drop/core-package/recipes',
										},
										subitems: [
											{
												item: {
													title: 'Isolating experiences',
													slug: 'isolating-experiences',
													to: '/components/pragmatic-drag-and-drop/core-package/recipes/isolating-experiences',
												},
												subitems: [],
											},
											{
												item: {
													title: 'Typing data',
													slug: 'typing-data',
													to: '/components/pragmatic-drag-and-drop/core-package/recipes/typing-data',
												},
												subitems: [],
											},
											{
												item: {
													title: 'Virtualization',
													slug: 'virtualization',
													to: '/components/pragmatic-drag-and-drop/core-package/recipes/virtualization',
												},
												subitems: [],
											},
											{
												item: {
													title: 'Deferred loading',
													slug: 'deferred-loading',
													to: '/components/pragmatic-drag-and-drop/core-package/recipes/deferred-loading',
												},
												subitems: [],
											},
										],
									},
									{
										item: {
											title: 'Testing',
											slug: 'testing',
											to: '/components/pragmatic-drag-and-drop/core-package/testing',
										},
										subitems: [],
									},
									{
										item: {
											title: 'Upgrade guides',
											slug: 'upgrade-guides',
											to: '/components/pragmatic-drag-and-drop/core-package/upgrade-guides',
										},
										subitems: [],
									},
									{
										item: {
											title: 'Changelog',
											slug: 'changelog',
											to: '/components/pragmatic-drag-and-drop/core-package/changelog',
										},
										subitems: [],
									},
								],
							},
							{
								item: {
									title: 'Optional packages',
									slug: 'optional-packages',
									to: '/components/pragmatic-drag-and-drop/optional-packages',
								},
								subitems: [
									{
										item: {
											title: 'Flourish',
											slug: 'flourish',
											to: '/components/pragmatic-drag-and-drop/optional-packages/flourish',
										},
										subitems: [
											{
												item: {
													title: 'Trigger post move flash',
													slug: 'trigger-post-move-flash',
													to: '/components/pragmatic-drag-and-drop/optional-packages/flourish/trigger-post-move-flash',
												},
												subitems: [],
											},
										],
									},
									{
										item: {
											title: 'React accessibility',
											slug: 'react-accessibility',
											to: '/components/pragmatic-drag-and-drop/optional-packages/react-accessibility',
										},
										subitems: [
											{
												item: {
													title: 'Drag handle button',
													slug: 'drag-handle-button',
													to: '/components/pragmatic-drag-and-drop/optional-packages/react-accessibility/drag-handle-button',
												},
												subitems: [],
											},
										],
									},

									{
										item: {
											title: 'Hitbox',
											slug: 'hitbox',
											to: '/components/pragmatic-drag-and-drop/optional-packages/hitbox',
										},
										subitems: [],
									},
									{
										item: {
											title: 'React drop indicator',
											slug: 'react-drop-indicator',
											to: '/components/pragmatic-drag-and-drop/optional-packages/react-drop-indicator',
										},
										subitems: [],
									},
									{
										item: {
											title: 'Auto scroll',
											slug: 'auto-scroll',
											to: '/components/pragmatic-drag-and-drop/optional-packages/auto-scroll',
										},
										subitems: [],
									},
									{
										item: {
											title: 'Live region',
											slug: 'live-region',
											to: '/components/pragmatic-drag-and-drop/optional-packages/live-region',
										},
										subitems: [],
									},
									{
										item: {
											title: 'React beautiful dnd migration',
											slug: 'react-beautiful-dnd-migration',
											to: '/components/pragmatic-drag-and-drop/optional-packages/react-beautiful-dnd-migration',
										},
										subitems: [],
									},
									{
										item: {
											title: 'React beautiful dnd autoscroll',
											slug: 'react-beautiful-dnd-autoscroll',
											to: '/components/pragmatic-drag-and-drop/optional-packages/react-beautiful-dnd-autoscroll',
										},
										subitems: [],
									},
									{
										item: {
											title: 'Unit testing',
											slug: 'unit-testing',
											to: '/components/pragmatic-drag-and-drop/optional-packages/unit-testing',
										},
										subitems: [],
									},
								],
							},
							{
								item: {
									title: 'Web platform design constraints',
									slug: 'web-platform-design-constraints',
									to: '/components/pragmatic-drag-and-drop/web-platform-design-constraints',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Design guidelines',
									slug: 'design-guidelines',
									to: '/components/pragmatic-drag-and-drop/design-guidelines',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Accessibility guidelines',
									slug: 'accessibility-guidelines',
									to: '/components/pragmatic-drag-and-drop/accessibility-guidelines',
								},
								subitems: [],
							},
						],
					},
				],
			},
			{
				item: {
					title: 'Tooling',
				},
				subitems: [
					{
						item: {
							title: 'App provider',
							slug: 'app-provider',
							to: '/components/app-provider',
							status: {
								type: 'beta',
							},
						},
						subitems: [],
					},
					{
						item: {
							title: 'ESLint plugin',
							slug: 'eslint-plugin-design-system',
							to: '/components/eslint-plugin-design-system',
						},

						subitems: [
							{
								item: {
									title: 'Consistent css prop usage',
									slug: 'consistent-css-prop-usage',
									to: '/components/eslint-plugin-design-system/consistent-css-prop-usage',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Ensure design token usage',
									slug: 'ensure-design-token-usage',
									to: '/components/eslint-plugin-design-system/ensure-design-token-usage',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Ensure design token usage preview',
									slug: 'ensure-design-token-usage-preview',
									to: '/components/eslint-plugin-design-system/ensure-design-token-usage-preview',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Ensure icon color',
									slug: 'ensure-icon-color',
									to: '/components/eslint-plugin-design-system/ensure-icon-color',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Expand motion shorthand',
									slug: 'expand-motion-shorthand',
									to: '/components/eslint-plugin-design-system/expand-motion-shorthand',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Icon label',
									slug: 'icon-label',
									to: '/components/eslint-plugin-design-system/icon-label',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No banned imports',
									slug: 'no-banned-imports',
									to: '/components/eslint-plugin-design-system/no-banned-imports',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No css tagged template expression',
									slug: 'no-css-tagged-template-expression',
									to: '/components/eslint-plugin-design-system/no-css-tagged-template-expression',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No dark theme vr tests',
									slug: 'no-dark-theme-vr-tests',
									to: '/components/eslint-plugin-design-system/no-dark-theme-vr-tests',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No deprecated apis',
									slug: 'no-deprecated-apis',
									to: '/components/eslint-plugin-design-system/no-deprecated-apis',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No deprecated design token usage',
									slug: 'no-deprecated-design-token-usage',
									to: '/components/eslint-plugin-design-system/no-deprecated-design-token-usage',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No deprecated imports',
									slug: 'no-deprecated-imports',
									to: '/components/eslint-plugin-design-system/no-deprecated-imports',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No direct use of web platform drag and drop',
									slug: 'no-direct-use-of-web-platform-drag-and-drop',
									to: '/components/eslint-plugin-design-system/no-direct-use-of-web-platform-drag-and-drop',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No empty styled expression',
									slug: 'no-empty-styled-expression',
									to: '/components/eslint-plugin-design-system/no-empty-styled-expression',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No exported css',
									slug: 'no-exported-css',
									to: '/components/eslint-plugin-design-system/no-exported-css',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No exported keyframes',
									slug: 'no-exported-keyframes',
									to: '/components/eslint-plugin-design-system/no-exported-keyframes',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No html anchor',
									slug: 'no-html-anchor',
									to: '/components/eslint-plugin-design-system/no-html-anchor',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No html button',
									slug: 'no-html-button',
									to: '/components/eslint-plugin-design-system/no-html-button',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No invalid css map',
									slug: 'no-invalid-css-map',
									to: '/components/eslint-plugin-design-system/no-invalid-css-map',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No keyframes tagged template expression',
									slug: 'no-keyframes-tagged-template-expression',
									to: '/components/eslint-plugin-design-system/no-keyframes-tagged-template-expression',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No icon spacing prop',
									slug: 'no-icon-spacing-prop',
									to: '/components/eslint-plugin-design-system/no-icon-spacing-prop',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No margin',
									slug: 'no-margin',
									to: '/components/eslint-plugin-design-system/no-margin',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No nested styles',
									slug: 'no-nested-styles',
									to: '/components/eslint-plugin-design-system/no-nested-styles',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No physical properties',
									slug: 'no-physical-properties',
									to: '/components/eslint-plugin-design-system/no-physical-properties',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No separator with list elements',
									slug: 'no-separator-with-list-elements',
									to: '/components/eslint-plugin-design-system/no-separator-with-list-elements',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No styled tagged template expression',
									slug: 'no-styled-tagged-template-expression',
									to: '/components/eslint-plugin-design-system/no-styled-tagged-template-expression',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No unsafe design token usage',
									slug: 'no-unsafe-design-token-usage',
									to: '/components/eslint-plugin-design-system/no-unsafe-design-token-usage',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No unsafe style overrides',
									slug: 'no-unsafe-style-overrides',
									to: '/components/eslint-plugin-design-system/no-unsafe-style-overrides',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No unsupported drag and drop libraries',
									slug: 'no-unsupported-drag-and-drop-libraries',
									to: '/components/eslint-plugin-design-system/no-unsupported-drag-and-drop-libraries',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Prefer primitives',
									slug: 'prefer-primitives',
									to: '/components/eslint-plugin-design-system/prefer-primitives',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use button group label',
									slug: 'use-button-group-label',
									to: '/components/eslint-plugin-design-system/use-button-group-label',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use cx function in xcss',
									slug: 'use-cx-function-in-xcss',
									to: '/components/eslint-plugin-design-system/use-cx-function-in-xcss',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use datetime picker calendar button',
									slug: 'use-datetime-picker-calendar-button',
									to: '/components/eslint-plugin-design-system/use-datetime-picker-calendar-button',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use drawer label',
									slug: 'use-drawer-label',
									to: '/components/eslint-plugin-design-system/use-drawer-label',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use heading',
									slug: 'use-heading',
									to: '/components/eslint-plugin-design-system/use-heading',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use heading level in spotlight card',
									slug: 'use-heading-level-in-spotlight-card',
									to: '/components/eslint-plugin-design-system/use-heading-level-in-spotlight-card',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use href in link item',
									slug: 'use-href-in-link-item',
									to: '/components/eslint-plugin-design-system/use-href-in-link-item',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use latest xcss syntax',
									slug: 'use-latest-xcss-syntax',
									to: '/components/eslint-plugin-design-system/use-latest-xcss-syntax',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use latest xcss syntax typography',
									slug: 'use-latest-xcss-syntax-typography',
									to: '/components/eslint-plugin-design-system/use-latest-xcss-syntax-typography',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use menu section title',
									slug: 'use-menu-section-title',
									to: '/components/eslint-plugin-design-system/use-menu-section-title',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use modal dialog close button',
									slug: 'use-modal-dialog-close-button',
									to: '/components/eslint-plugin-design-system/use-modal-dialog-close-button',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use onboarding spotlight label',
									slug: 'use-onboarding-spotlight-label',
									to: '/components/eslint-plugin-design-system/use-onboarding-spotlight-label',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use popup label',
									slug: 'use-popup-label',
									to: '/components/eslint-plugin-design-system/use-popup-label',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use pressable motion',
									slug: 'use-pressable-motion',
									to: '/components/eslint-plugin-design-system/use-pressable-motion',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use primitives',
									slug: 'use-primitives',
									to: '/components/eslint-plugin-design-system/use-primitives',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use primitives text',
									slug: 'use-primitives-text',
									to: '/components/eslint-plugin-design-system/use-primitives-text',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use tag group label',
									slug: 'use-tag-group-label',
									to: '/components/eslint-plugin-design-system/use-tag-group-label',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use tokens motion',
									slug: 'use-tokens-motion',
									to: '/components/eslint-plugin-design-system/use-tokens-motion',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use tokens space',
									slug: 'use-tokens-space',
									to: '/components/eslint-plugin-design-system/use-tokens-space',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use tokens typography',
									slug: 'use-tokens-typography',
									to: '/components/eslint-plugin-design-system/use-tokens-typography',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use visually hidden',
									slug: 'use-visually-hidden',
									to: '/components/eslint-plugin-design-system/use-visually-hidden',
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Storybook addon',
							slug: 'storybook-addon-design-system',
							to: '/components/storybook-addon-design-system',
						},
						subitems: [],
					},
					{
						item: {
							title: 'Stylelint plugin',
							slug: 'stylelint-design-system',
							to: '/components/stylelint-design-system',
						},
						subitems: [],
					},
					{
						item: {
							title: 'UI Styling Standard',
							slug: 'eslint-plugin-ui-styling-standard',
							to: '/components/eslint-plugin-ui-styling-standard',
						},
						subitems: [
							{
								item: {
									title: 'Enforce style prop',
									slug: 'enforce-style-prop',
									to: '/components/eslint-plugin-ui-styling-standard/enforce-style-prop',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Local cx xcss',
									slug: 'local-cx-xcss',
									to: '/components/eslint-plugin-ui-styling-standard/local-cx-xcss',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No array arguments',
									slug: 'no-array-arguments',
									to: '/components/eslint-plugin-ui-styling-standard/no-array-arguments',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No classname prop',
									slug: 'no-classname-prop',
									to: '/components/eslint-plugin-ui-styling-standard/no-classname-prop',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No container queries',
									slug: 'no-container-queries',
									to: '/components/eslint-plugin-ui-styling-standard/no-container-queries',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No css tagged template expression',
									slug: 'no-css-tagged-template-expression',
									to: '/components/eslint-plugin-ui-styling-standard/no-css-tagged-template-expression',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No dynamic styles',
									slug: 'no-dynamic-styles',
									to: '/components/eslint-plugin-ui-styling-standard/no-dynamic-styles',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No empty styled expression',
									slug: 'no-empty-styled-expression',
									to: '/components/eslint-plugin-ui-styling-standard/no-empty-styled-expression',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No exported css',
									slug: 'no-exported-css',
									to: '/components/eslint-plugin-ui-styling-standard/no-exported-css',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No exported keyframes',
									slug: 'no-exported-keyframes',
									to: '/components/eslint-plugin-ui-styling-standard/no-exported-keyframes',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No exported styles',
									slug: 'no-exported-styles',
									to: '/components/eslint-plugin-ui-styling-standard/no-exported-styles',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No global styles',
									slug: 'no-global-styles',
									to: '/components/eslint-plugin-ui-styling-standard/no-global-styles',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No important styles',
									slug: 'no-important-styles',
									to: '/components/eslint-plugin-ui-styling-standard/no-important-styles',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No imported style values',
									slug: 'no-imported-style-values',
									to: '/components/eslint-plugin-ui-styling-standard/no-imported-style-values',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No invalid css map',
									slug: 'no-invalid-css-map',
									to: '/components/eslint-plugin-ui-styling-standard/no-invalid-css-map',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No keyframes tagged template expression',
									slug: 'no-keyframes-tagged-template-expression',
									to: '/components/eslint-plugin-ui-styling-standard/no-keyframes-tagged-template-expression',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No nested selectors',
									slug: 'no-nested-selectors',
									to: '/components/eslint-plugin-ui-styling-standard/no-nested-selectors',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No styled',
									slug: 'no-styled',
									to: '/components/eslint-plugin-ui-styling-standard/no-styled',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No styled tagged template expression',
									slug: 'no-styled-tagged-template-expression',
									to: '/components/eslint-plugin-ui-styling-standard/no-styled-tagged-template-expression',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No unsafe selectors',
									slug: 'no-unsafe-selectors',
									to: '/components/eslint-plugin-ui-styling-standard/no-unsafe-selectors',
								},
								subitems: [],
							},
							{
								item: {
									title: 'No unsafe values',
									slug: 'no-unsafe-values',
									to: '/components/eslint-plugin-ui-styling-standard/no-unsafe-values',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Use compiled',
									slug: 'use-compiled',
									to: '/components/eslint-plugin-ui-styling-standard/use-compiled',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Atlaskit theme',
									slug: 'atlaskit-theme',
									to: '/components/eslint-plugin-ui-styling-standard/atlaskit-theme',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Consistent css prop usage',
									slug: 'consistent-css-prop-usage',
									to: '/components/eslint-plugin-ui-styling-standard/consistent-css-prop-usage',
								},
								subitems: [],
							},
							{
								item: {
									title: 'Convert props syntax',
									slug: 'convert-props-syntax',
									to: '/components/eslint-plugin-ui-styling-standard/convert-props-syntax',
								},
								subitems: [],
							},
						],
					},
				],
			},
			{
				item: { title: 'Deprecated' },
				subitems: [
					{
						item: {
							title: 'Atlassian navigation',
							slug: 'atlassian-navigation',
							to: '/components/atlassian-navigation',
							status: {
								type: 'deprecated',
							},
						},
						subitems: [],
					},
					{
						item: {
							title: 'Layout grid',
							slug: 'grid',
							to: '/components/grid',
							status: {
								type: 'deprecated',
							},
						},
						subitems: [],
					},
					{
						item: {
							title: 'Onboarding (spotlight)',
							slug: 'onboarding',
							to: '/components/onboarding',
							status: {
								type: 'deprecated',
							},
						},
						subitems: [
							{
								item: {
									title: 'Benefits modal',
									slug: 'benefits-modal',
									to: '/components/onboarding/benefits-modal',
									status: {
										type: 'deprecated',
									},
								},
								subitems: [],
							},
							{
								item: {
									title: 'Spotlight card',
									slug: 'spotlight-card',
									to: '/components/onboarding/spotlight-card',
									status: {
										type: 'deprecated',
									},
								},
								subitems: [],
							},
						],
					},
					{
						item: {
							title: 'Page layout',
							slug: 'page-layout',
							to: '/components/page-layout',
							status: {
								type: 'deprecated',
							},
						},
						subitems: [],
					},
					{
						item: {
							title: 'Side navigation',
							slug: 'side-navigation',
							to: '/components/side-navigation',
							status: {
								type: 'deprecated',
							},
						},
						subitems: [],
					},
				],
			},
		],
	},
	{
		item: { title: 'Rovo UI', to: '/rovo-ui', slug: 'rovo-ui' },
		subitems: [
			{
				item: { title: 'About Rovo UI', to: '/rovo-ui/about-rovo-ui', slug: 'about-rovo-ui' },
				subitems: [],
			},
			{
				item: {
					title: 'AI interaction guidelines',
					to: '/rovo-ui/ai-interaction-guidelines',
					slug: 'ai-interaction-guidelines',
				},
				subitems: [],
			},
			{
				item: { title: 'Foundations for Rovo' },
				subitems: [
					{
						item: { title: 'Color', to: '/rovo-ui/color', slug: 'color' },
						subitems: [],
					},
					{
						item: { title: 'Elevation', to: '/rovo-ui/elevation', slug: 'elevation' },
						subitems: [],
					},
					{
						item: { title: 'Motion', to: '/rovo-ui/motion', slug: 'motion' },
						subitems: [],
					},
					{
						item: {
							title: 'Voice and tone',
							to: '/rovo-ui/voice-and-tone',
							slug: 'voice-and-tone',
						},
						subitems: [],
					},
				],
			},
			{
				item: { title: 'Components for Rovo' },
				subitems: [
					{
						item: { title: 'Button', to: '/rovo-ui/button', slug: 'button' },
						subitems: [],
					},
					{
						item: {
							title: 'Generative border',
							to: '/rovo-ui/generative-border',
							slug: 'generative-border',
						},
						subitems: [],
					},
					{
						item: { title: 'Icon', to: '/rovo-ui/icon', slug: 'icon' },
						subitems: [],
					},
					{
						item: { title: 'Illustration', to: '/rovo-ui/illustration', slug: 'illustration' },
						subitems: [],
					},
					{
						item: { title: 'Skills tag', to: '/rovo-ui/skills-tag', slug: 'skills-tag' },
						subitems: [],
					},
				],
			},
		],
	},
	{
		item: { title: 'Tools', to: '/tools', slug: 'tools' },
		subitems: [],
	},
	{
		item: { title: 'Release phases', to: '/release-phases', slug: 'release-phases' },
		subitems: [],
	},
	{
		item: { title: 'Contact us', to: '/contact-us', slug: 'contact-us' },
		subitems: [],
	},
];
